import { useState, useCallback } from 'react';

import { createRoutePointId } from 'shared/utils';

import { ListItem } from 'common/types';
import { GeoCoords } from 'common/utils';

import { EMPTY_ARRAY } from 'shared/constants';

import { Dispatch, SetStateAction } from 'react';
import { DropResult } from 'react-beautiful-dnd';

export type DragAndDropError = string | null;

export interface DragAndDropPointValue extends Partial<ListItem> {
  type?: string;
  coordinates?: GeoCoords;
  metadata?: {
    placeId: string;
  } | string;
}

export interface DragAndDropPoint {
  id: string;
  value: DragAndDropPointValue;
}

export type SetDragAndDropPoints = Dispatch<SetStateAction<DragAndDropPoint[]>>;
export type CreateDragAndDropPoint = () => void;
export type HandleDragAndDropPointsDragEnd = (result: DropResult) => void;
export type UpdateDragAndDropPoint = (id: DragAndDropPoint['id'], value: DragAndDropPoint['value']) => void;
export type DeleteDragAndDropPoint = (id: DragAndDropPoint['id']) => void;
export type ResetDragAndDroptPoints = () => void;
export type SetDragAndDropPointErrors = Dispatch<SetStateAction<DragAndDropError[]>>;
export type ResetDragAndDropPointError = (index: number) => void;

const getRoutePoint = (value: DragAndDropPointValue = {}): DragAndDropPoint => ({
  id: createRoutePointId(),
  value
});

export const useDragAndDropHandlers = (defaultPoints: DragAndDropPoint[] = EMPTY_ARRAY) => {
  const [points, setPoints] = useState<DragAndDropPoint[]>(defaultPoints);
  const [pointErrors, setPointErrors] = useState<DragAndDropError[]>([]);

  const createPoint = useCallback(() => {
    setPoints((prevPoints) => {
      if (prevPoints.length === 0) return [getRoutePoint()];
      const lastIndex = prevPoints.length - 1;
      return [
        ...prevPoints.slice(0, lastIndex),
        getRoutePoint(),
        prevPoints[lastIndex]
      ];
    });
  }, []);

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    setPoints((prevPoints) => {
      const newPoints = [...prevPoints];
      const [removed] = newPoints.splice(source.index, 1);
      newPoints.splice(destination.index, 0, removed);
      return newPoints;
    });
  }, []);

  const updatePoint = useCallback((id: DragAndDropPoint['id'], value: DragAndDropPoint['value']) => {
    setPoints((prevPoints) =>
      prevPoints.map((point) =>
        point.id === id ? { ...point, value } : point
      )
    );
  }, [setPoints]);

  const deletePoint = useCallback((id: DragAndDropPoint['id']) => {
    setPoints((prevPoints) => prevPoints.filter((point) => point.id !== id));
  }, []);

  const resetPoints = useCallback(() => {
    setPoints(defaultPoints);
  }, [defaultPoints]);

  const resetPointError = useCallback((index: number) => {
    setPointErrors((errors) =>
      errors.map((error, i) => (i === index ? null : error))
    );
  }, []);

  return [
    points,
    setPoints,
    createPoint,
    updatePoint,
    deletePoint,
    resetPoints,
    handleDragEnd,

    pointErrors,
    setPointErrors,
    resetPointError
  ] as const;
};